/**
 * Удаление бренда: что при этом происходит и почему это отдельный файл.
 *
 * Отдельно от экранов по той же причине, что и удаление клиента: здесь живут
 * решения, у которых есть правильный и неправильный ответ, а глазами их не
 * проверить. Разработчик не видит ни второй вкладки, в которой этот бренд уже
 * удалён, ни двойного щелчка, ни бренда, выбранного областью работы в момент,
 * когда его убирают. Плюс мест вызова два — карточка и строка списка, — и
 * второй обработчик, написанный рядом, разъедется с первым на первой же правке.
 *
 * Четыре решения, ради которых всё это написано.
 *
 * 1. **Одно действие вместо двух шагов.** Живой бренд не надо сначала
 *    архивировать: сервер архивирует его сам, теми же правилами и в той же
 *    транзакции (`POST /brands/{brandId}/hide`). Два подтверждения ради одного
 *    намерения — плохой продукт, а два запроса из браузера дают промежуточное
 *    состояние при обрыве связи.
 * 2. **`404` — это успех.** Повтор отвечает `404`, а не `409`: бренда для API
 *    больше нет. Второй щелчок и вторая вкладка приходят сюда же, и красная
 *    плашка «не найдено» пугала бы ровно там, где человек получил именно то,
 *    чего хотел. Решает это `removalMeansGone`, а не разметка экрана.
 * 3. **Область не остаётся стоять на удалённом бренде.** Он выбран в
 *    переключателе наверху, и после удаления половина разделов спрашивала бы
 *    данные бренда, которого для API больше нет.
 * 4. **Исчезновение объясняется словом.** Карточка закрывается, строка уходит
 *    из таблицы — без единого слова это читается как сбой, особенно на ветке
 *    `404`. Поэтому уход на список брендов несёт с собой короткую строку.
 *
 * Ручка называется `hide`, кнопка называется «Удалить», и расхождение наружу не
 * течёт: для покупателя бренд именно удаляется — исчезает навсегда, вернуть его
 * самостоятельно нельзя (`voice.md`: «Name things the way a buyer would»).
 * Ни одно слово про скрытие человеку не показывается — ни в кнопке, ни в
 * подтверждении, ни в отказе: успех приходит пустым `204`, а повтор `404`,
 * который кабинет считает успехом.
 */

import { useCallback, useEffect, useRef } from 'react';

import { api } from '../../api/client';
import { ApiError } from '../../api/problem';
import { countOf } from '@shared/src/format';
import type { components } from '../../api/schema';

type Brand = components['schemas']['Brand'];

/**
 * Экран, с которого начали, ещё открыт?
 *
 * Спрашивается перед уходом на список. Окно подтверждения закрывается первой
 * строкой обработчика — иначе второй щелчок уходит вторым запросом, — и за
 * время запроса человек успевает уйти в другой раздел, открыть другой бренд или
 * переключить организацию. Уводить его оттуда на список брендов нельзя: он туда
 * не собирался, а виноватым выглядел бы раздел, в который он только что зашёл.
 * Бренд при этом всё равно удалён, область перечитана, и на списке он его
 * больше не встретит.
 */
export function useStillHere(): () => boolean {
  const here = useRef(true);
  useEffect(() => {
    here.current = true;
    return () => {
      here.current = false;
    };
  }, []);
  return useCallback(() => here.current, []);
}

/** Кого удаляем: имя нужно, чтобы сказать о случившемся на списке. */
export interface RemovedBrand {
  id: string;
  name: string;
}

/** Что уезжает на список брендов вместе с переходом. */
export interface RemovedState {
  removed: string;
}

export interface RemoveDeps {
  /**
   * Снять бренд с области работы, если выбран был он.
   *
   * Правило одно на оба места вызова — `dropRemovedBrand` ниже; сюда оно
   * приходит зависимостью, потому что область живёт выше экранов и в приёмке
   * её нет.
   */
  dropSelection: () => void;
  /** Перечитать область: переключатель, списки, счётчики, вкладка «Архив». */
  reload: () => void;
  /** Уйти на список брендов и сказать там, что бренда больше нет. */
  navigate: (to: string, options: { replace: boolean; state: RemovedState }) => void;
}

/**
 * Отказ, за которым бренда всё равно уже нет.
 *
 * Наверх из клиента API приходит только `ApiError`: обрыв сети, не-JSON от края
 * CDN и пустое тело завёрнуты в него же, а `status` берётся из HTTP-ответа, а
 * не из тела (`readProblem` в `api/client.ts`). Поэтому спрашивается ровно код.
 *
 * `404` здесь однозначен, в отличие от `409` на архивации: операция удаления
 * читает бренд обычным загрузчиком, который скрытых не видит, — то есть
 * «не найден» приходит и на повторе, и на бренде, удалённом в соседней вкладке,
 * и на чужом бренде. Все три случая для человека означают одно: бренда нет.
 */
export function removalMeansGone(cause: unknown): boolean {
  return cause instanceof ApiError && cause.problem.status === 404;
}

/** Строка, которую человек читает на списке после удаления. */
export function removedNote(name: string): string {
  return `${name} — бренд удалён. Замеры по нему остановлены, история осталась в базе.`;
}

/**
 * Область, которой хватает, чтобы снять с себя удалённый бренд.
 *
 * Ровно то же правило, по которому область ведёт себя при архивации: удалённый
 * бренд не может остаться выбранным — по нему нет ни замеров, ни чисел, и
 * половина разделов показала бы пустоту без объяснения. Единственный
 * оставшийся живой выбирается сам: спрашивать «какой бренд смотрим» там, где
 * вариант один, незачем.
 */
export interface SelectionScope {
  /**
   * Выбор спрашивается ФУНКЦИЕЙ, а не берётся значением, и это половина смысла.
   *
   * Окно закрывается первой строкой, запрос летит, и за это время человек
   * успевает переключить бренд наверху — в обе стороны. Ушёл с удаляемого:
   * снимок сказал бы «выбран он» и сбросил бы уже НОВЫЙ выбор. Перешёл на него:
   * снимок сказал бы «выбран другой», и область осталась бы стоять на бренде,
   * которого больше нет. Свежий ответ знает только сама область.
   */
  currentBrandId: () => string | null;
  liveBrands: readonly Brand[];
  selectBrand: (id: string | null) => void;
}

/**
 * Снять с области бренд, которого больше нет.
 *
 * Замена ищется по списку ДО перезагрузки области: в нём этот бренд ещё живой,
 * поэтому он и вычитается явно. Список тут имеет право быть чуть несвежим —
 * это ВЫБОР замены, а не решение о сбросе, и следом всё равно идёт
 * `reload()`, который пересчитает область по ответу сервера. Решение о сбросе
 * несвежим быть не имеет права, и потому спрашивается функцией.
 *
 * Чужой выбор не трогается: человек, удаливший бренд из списка, не должен
 * терять место, в котором работал.
 */
export function dropRemovedBrand(scope: SelectionScope, brandId: string): void {
  if (scope.currentBrandId() !== brandId) return;
  const rest = scope.liveBrands.filter((b) => b.id !== brandId);
  const only = rest.length === 1 ? rest[0] : undefined;
  scope.selectBrand(only ? only.id : null);
}

/**
 * Удалить бренд и привести кабинет в порядок после этого.
 *
 * Порядок обязателен: сначала ответ сервера, потом уборка. Сброс выбора и уход
 * со страницы до ответа увели бы человека с бренда, который остался на месте, —
 * на любом отказе, кроме `404`, экран обязан выглядеть так, будто ничего не
 * нажимали.
 *
 * Ключа идемпотентности нет, и это не забывчивость: контракт его у этой
 * операции не объявляет, повтор ничего второго не заводит — он приходит на
 * `404`, а лишний обязательный заголовок сервис отбивает `422`.
 */
export async function removeBrand(brand: RemovedBrand, deps: RemoveDeps): Promise<void> {
  try {
    await api.post<void>(`/brands/${brand.id}/hide`);
  } catch (cause) {
    // Бренда уже нет — человек получил то, чего хотел. Всё остальное
    // пробрасывается как есть: текст отказа пишет сервер.
    if (!removalMeansGone(cause)) throw cause;
  }
  deps.dropSelection();
  deps.reload();
  deps.navigate('/brands', {
    replace: true,
    state: { removed: removedNote(brand.name) },
  });
}

/**
 * Что именно исчезнет.
 *
 * Два текста, а не один с оговорками: у архивного бренда замеры уже
 * остановлены, квота уже свободна и имя уже отдано. Обещать это второй раз —
 * врать в мелочи ровно там, где человек читает внимательнее всего.
 *
 * Обещания одинаковы в обоих: публичные ссылки гаснут, история остаётся,
 * вернуть бренд самостоятельно нельзя. Последнее — главное, что человек обязан
 * прочитать до нажатия: возврата из удаления в продукте нет, это обращение в
 * поддержку.
 */
export function RemoveConfirmBody({
  name,
  status,
  questions,
}: {
  name: string;
  status: Brand['status'];
  questions: number;
}) {
  return (
    <>
      {status === 'archived' ? (
        <p className="dlg-lead">
          Бренд «{name}» уйдёт из архива и больше нигде не появится. Публичные ссылки на
          его отчёты перестанут открываться.
        </p>
      ) : (
        <>
          {/* Согласование сказуемого с числительным здесь не решается ничем:
              «1 вопрос освободят» и «21 вопрос освободят» одинаково неверны, а
              «освободит» неверно на двойке. Поэтому счёт стоит дополнением —
              «на 1 вопрос», «на 2 вопроса», «на 21 вопрос», — и `countOf`
              хватает целиком. Квоты может не быть вовсе: у бренда, заведённого
              минуту назад, ядра ещё нет, и обещать возврат нуля вопросов
              значит врать в мелочи там, где читают внимательнее всего. */}
          <p className="dlg-lead">
            Замеры по «{name}» остановятся, а название снова можно будет занять.
            {questions > 0
              ? ` Квота тарифа освободится на ${countOf(questions, 'вопрос', 'вопроса', 'вопросов')}.`
              : ''}
          </p>
          <p className="dlg-lead">
            Бренд уйдёт из кабинета совсем: ни в списках, ни в архиве, ни в портфеле его
            не будет. Публичные ссылки на его отчёты перестанут открываться.
          </p>
        </>
      )}
      <p className="dlg-lead">
        История замеров сохранится в базе, но открыть её из кабинета будет нельзя. Вернуть
        бренд самостоятельно не получится — если удалили по ошибке, напишите в поддержку.
      </p>
    </>
  );
}
